<template>
  <section aria-label="Product Collection" ref="list" class="Collection Collection--Filters page-width">
    <Facets v-if="collection.filters" :facets="collection.filters" :terms="terms" @apply-filters="applyFilters" @reset-cursor="resetCursor" />
    <div class="Collection-GridWrapper">
      <div class="Collection-Grid">
        <template v-if="isEmpty">
          <EmptyCollection :terms="terms" />
        </template>
        <template v-else>
          <ProductCard v-for="item in items" :key="item" :item="item" :ready="promoReady" :appliedFilters="appliedFilters" :terms="qvTerms" />
        </template>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { fetchStorefront } from '@utils/GraphQLClient';
import { PRODUCTS_FROM_COLLECTION_QUERY } from './Composables/Queries';
import { pubSub } from '@utils/PubSub';
import { deepClone } from '@utils/performance.js';

import Facets from './Facets.vue';
import EmptyCollection from './EmptyCollection.vue';
import ProductCard from './ProductCard.vue';

const props = defineProps({
  handle: String,
  terms: Object,
  qvTerms: Object,
});

const items = ref([]);
const list = ref(null);
const batchSize = ref(12);
const isEmpty = ref(false);
const observer = ref(null);
const endCursor = ref(null);
const promoReady = ref(false);
const hasNextPage = ref(true);
const isFetching = ref(false);
const collection = ref({});
const appliedFilters = ref([]);
const variantsFetched = ref(1);

const unobserve = () => {
  console.log('Unobserving...');
  if (observer.value) observer.value.disconnect();
};

const observeLastCard = () => {
  unobserve();
  setTimeout(() => {
    const lastCard = list.value?.querySelector('.js-product-card:last-child');
    if (lastCard) {
      console.log('Observing last card:', lastCard);
      observer.value.observe(lastCard);
    } else {
      console.warn('No last card found to observe');
    }
  }, 0);
};

const applyFilters = async (selections) => {
  const filters = selections.reduce((arr, item) => {
    const data = Object.entries(item)[0];
    const key = data[0];
    const value = data[1];

    const matchingFacetIndex = collection.value.filters.findIndex((filter) => {
      return filter.id === key;
    });

    const facet = collection.value.filters[matchingFacetIndex].values.find((filter) => {
      const isColor = filter.id.includes('color');

      if (!isColor) return filter.label === value;

      const parsedGraphId = value.split('gid://shopify/Metaobject/')[1].trim();

      return filter.id.includes(parsedGraphId);
    });

    arr.push(JSON.parse(facet.input));

    return arr;
  }, []);

  appliedFilters.value = filters;
  variantsFetched.value = 100;
  isFetching.value = false;
  hasNextPage.value = true;
  endCursor.value = null;
  items.value = [];
  await fetchProducts();
};

const initializeObserver = () => {
  if (observer.value) unobserve();
  console.log('Observing...');
  observer.value = new IntersectionObserver(
    (entries) => {
      const { isIntersecting } = entries[0];
      console.log('Observer callback triggered, isIntersecting:', isIntersecting);
      if (isIntersecting) fetchProducts();
    },
    { threshold: 0.6 }
  );
};

const updateNestedFilterValues = (data) => {
  /**
   * Updates each object in the array by adding an `applied: false` property
   * to every object inside the `values` array (if `values` exists).
   *
   * @param {Array} data - The input array containing category objects.
   * @returns {Array} - A new array with updated objects, including `applied: false` in `values`.
   */
  return data.map((category) => ({
    ...category,
    values: category.values ? category.values.map((value) => ({ ...value, applied: false })) : category.values,
  }));
};

const fetchProducts = async () => {
  if (isFetching.value || !hasNextPage.value) {
    unobserve();
    return;
  }

  const batch = [];
  isFetching.value = true;
  collection.value = collection.value || {};
  collection.value.title = collection.value?.title || '';
  collection.value.filters = collection.value?.filters || [];
  endCursor.value = endCursor.value || null;
  console.log('Fetching Products...');
  try {
    const request = {
      query: PRODUCTS_FROM_COLLECTION_QUERY,
      variables: {
        handle: props.handle,
        first: batchSize.value,
        after: endCursor.value,
        sortKey: 'COLLECTION_DEFAULT',
        filters: appliedFilters.value,
        variantsRetrieved: variantsFetched.value,
      },
    };
    const { data } = await fetchStorefront(request);
    const products = data?.collection?.products ?? {};

    batch.push(...products.edges.map((edge) => edge.node));
    endCursor.value = products.pageInfo.endCursor;

    if (collection.value.filters.length <= 0) {
      collection.value.filters = updateNestedFilterValues(products.filters);
    }

    hasNextPage.value = products.pageInfo.hasNextPage;
    collection.value.title = collection.value?.title || data.collection.title;
    const results = [...items.value, ...batch];
    const values = displayExposedVariants(results);

    items.value = values;

    isEmpty.value = items.value.length <= 0;
  } catch (error) {
    console.error(`Collection query for ${props.handle} failed.`, error);
    return [];
  } finally {
    isFetching.value = false;
    console.log('Fetched Products: ', items.value.length);
  }
};

/**
 * Filters and exposes only the unique variants from a collection based on the selected color facets.
 *
 * This function finds color-related filters applied by the user, extracts matching product variants,
 * ensures only unique colors are included, and updates the displayed items accordingly.
 */
const displayExposedVariants = (values) => {
  /**
   * Extracts the relevant metafield values from applied filters.
   * Some filters may have `variantMetafield`, while others may have `productMetafield`.
   *
   * @const {Array<string>} facets - An array of selected facet values.
   */
  const facets = appliedFilters.value.map((facet) => facet.variantMetafield || facet.productMetafield);

  // If no facets exist, exit early
  if (facets.length <= 0) return values;

  /**
   * Filters to find only the facets related to color grouping.
   *
   * @const {Array<Object>} colorFacetsSelected - An array of facets related to the 'color_group'.
   */
  const colorFacetsSelected = facets.filter((facet) => facet.key === 'color_group');

  /**
   * Checks whether any color facets have been applied.
   *
   * @const {boolean} colorApplied - A flag indicating if color filters exist.
   */
  const colorApplied = colorFacetsSelected.length > 0;

  // If no color filters are applied, exit early
  if (!colorApplied) return values;

  /**
   * Extracts the selected color values from the color facets.
   *
   * @const {Array<string>} colorsSelected - An array of selected color values.
   */
  const colorsSelected = colorFacetsSelected.map((facet) => facet.value);

  /**
   * Creates a deep copy of the original `items` array to avoid mutating the original data.
   *
   * @const {Array<Object>} itemsInCollection - A deep-cloned version of `items.value`.
   */
  const itemsInCollection = deepClone(values);

  /**
   * Iterates through each item in the collection and filters variants that match the selected colors.
   *
   * @const {Array<Object>} matches - An array of items with only the unique variants exposed.
   */
  const matches = itemsInCollection.reduce((arr, item) => {
    /**
     * Retrieves all variants for the current item.
     *
     * @const {Array<Object>} variants - The variants of the current item.
     */
    const variants = item.variants.nodes;

    /**
     * Filters variants that have a matching `colorGroup.value` in the selected colors.
     *
     * @const {Array<Object>} matchingVariantsToSelections - Variants that match the selected color filters.
     */
    const matchingVariantsToSelections = variants.filter((variant) => {
      const hasColorGroup = variant.colorGroup?.value || false;
      return hasColorGroup && colorsSelected.includes(variant.colorGroup.value);
    });

    /**
     * Filters out duplicate variants by ensuring only one variant per unique color is included.
     *
     * @const {Array<Object>} uniqueVariants - A filtered array of variants, each having a unique color.
     */
    const uniqueVariants = matchingVariantsToSelections.filter((currentVariant, currentIndex, data) => {
      return (
        currentIndex ===
        data.findIndex((comparisonVariant) => {
          /**
           * Extracts the color value from the `selectedOptions` array for comparison.
           *
           * @const {string | undefined} comparingColor - The color of the comparison variant.
           * @const {string | undefined} currentColor - The color of the current variant.
           */
          const comparingColor = comparisonVariant.selectedOptions.find((option) => option.name === 'Color')?.value;
          const currentColor = currentVariant.selectedOptions.find((option) => option.name === 'Color')?.value;

          return comparingColor === currentColor;
        })
      );
    });

    /**
     * Creates a new array where each unique variant is wrapped inside a cloned version of the original item.
     * This ensures that each unique variant is still associated with its original product.
     *
     * @const {Array<Object>} exposedVariants - An array of item objects with only one unique variant inside each.
     */
    const exposedVariants = uniqueVariants.reduce((data, variant) => {
      const clonedItem = deepClone(item);
      clonedItem.variants.nodes = [variant];

      data.push(clonedItem);
      return data;
    }, []);

    // Add the exposed variants to the accumulated result array
    arr.push(...exposedVariants);
    return arr;
  }, []);

  // Update the displayed items with only the filtered and unique variants
  return [...matches];
};

const mountPromo = () => {
  pubSub.unsubscribe('dy:promo:init', mountPromo);
  pubSub.publish('dy:promo:ready');
};

const mountFallback = () => {
  pubSub.unsubscribe('dy:promo:init', mountFallback);
  pubSub.publish('dy:promo:ready');
};

const setPromo = () => {
  pubSub.unsubscribe('dy:promo:ready', setPromo);
  promoReady.value = true;
};

const resetCursor = () => {
  console.log('Resetting Collection Cursor...');
  appliedFilters.value = [];
  variantsFetched.value = 1;
  isFetching.value = false;
  hasNextPage.value = true;
  endCursor.value = null;
  items.value = [];
};

onMounted(async () => {
  pubSub.subscribe('dy:promo:init', mountPromo, mountFallback);
  pubSub.subscribe('dy:promo:ready', setPromo);
  initializeObserver();
  watch(() => items.value, observeLastCard, { flush: 'post' });
  await fetchProducts();
});
</script>
